import { Box } from '@mui/material'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { DisplayUserNames } from 'components/core/search/users'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { ResourceDisplayName } from 'components/pages/entitlement/resource'
import { QUERIES } from 'components/pages/explore/query/Query'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppResource, AppResourceType, SearchAppResourcesWithDeactivatedOwnerRequest } from 'pbts/c1/api/app/v1/app_resource'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { AppResourceValueAndInactiveAppOwner, searchAppResourcesWithDeactivatedOwner } from 'redux_toolkit/app-resources'
import { ExportData } from './ExportData'
import { StructuredSearchStyles } from './styles'

const exportDataActionRequest = ExportDataRequest.create({
    queryId: QueryId.APP_RESOURCES_WITH_DEACTIVATED_OWNER,
    request: {
        oneofKind: 'searchAppResourcesWithDeactivatedOwnerRequest',
        searchAppResourcesWithDeactivatedOwnerRequest: SearchAppResourcesWithDeactivatedOwnerRequest.create({ expandMask: { paths: ['*'] } }),
    },
})

const cols: Array<C1TableReduxColumn<AppResourceValueAndInactiveAppOwner>> = [
    {
        field: 'resource',
        label: <FormattedMessage id="components_resource_rollup_list_type" defaultMessage="Resource" />,
        Component: ({ row }) => <ResourceDisplayName
            resource={row?.appResource || AppResource.create({})}
            appName={row?.appResource?.app?.displayName || ''}
            resourceType={row?.appResource?.appResourceType || AppResourceType.create({})}
            includeDetails
            includeType
        />,
    },
    {
        field: 'app',
        label: <FormattedMessage id="components_resource_rollup_list_app" defaultMessage="App" />,
        Component: ({ row }) => <AppDisplayName app={row?.appResource?.app || App.create({})} />,

    },
    {
        field: 'resource-owner',
        label: <FormattedMessage id="components_resource_rollup_list_resource" defaultMessage="Resource Owner" />,
        Component: ({ row }) => <DisplayUserNames
            expandable
            proposedOptions={row.appResourceOwners}
        />,
    },
]

export const AppResourcesWithDeactivatedOwnerReport: FC = () => {
    const [page, setPage] = useState<number>(0)

    const { data, isError, isLoading, getPage, canLoadMore, error } =
    searchAppResourcesWithDeactivatedOwner.useSearchAppResourcesWithDeactivatedOwner(
        SearchAppResourcesWithDeactivatedOwnerRequest.create({
            expandMask: { paths: ['*'] },
        }),
        {},
    )

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.AppResourcesWithDeactivatedOwner)

    const userTable = useMemo(
        () => (
            <C1TableRedux
                data={data}
                cols={cols}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                onPageChange={onPageChange}
                error={error}
                page={page}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    return <>
        <Box sx={StructuredSearchStyles.exportDataBox}>
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Box>
        <List<AppUserValue> table={userTable} />
    </>
}
